import { NylasEmailClient } from 'pages/dashboard/pages/dispatcher/pages/dispatcher-emails/controllers'
import { EmailAccount } from 'pages/dashboard/pages/dispatcher/pages/dispatcher-emails/controllers/types'

type ShowToastFunction = (message: string, type: 'success' | 'danger') => void

export const handleNylasEmailAdd = async (onSuccess?: (accounts: EmailAccount[]) => void, onError?: (error: string) => void, showToast?: ShowToastFunction) => {
    try {
        const params = new URLSearchParams({
            client_id: process.env.REACT_APP_NYLAS_CLIENT_ID!,
            redirect_uri: process.env.REACT_APP_NYLAS_REDIRECT_URI!,
            response_type: 'code',
            options: 'smtp_required',
        })

        const authUrl = `${process.env.REACT_APP_NYLAS_API_URL}/v3/connect/auth?${params.toString()}`
        const authWindow = window.open(authUrl.toString(), 'Outlook Auth', 'width=500,height=600,scrollbars=yes')

        // Track if auth flow completed (success or error) and if callbacks have been called
        let authFlowCompleted = false
        let callbackInvoked = false

        // Helper to safely invoke error callback only once
        const invokeErrorCallback = (errorMessage: string) => {
            if (callbackInvoked) return
            callbackInvoked = true
            if (errorMessage) {
                showToast?.(errorMessage, 'danger')
            }
            onError?.(errorMessage)
        }

        // Helper to safely invoke success callback only once
        const invokeSuccessCallback = (accounts: EmailAccount[]) => {
            if (callbackInvoked) return
            callbackInvoked = true
            onSuccess?.(accounts)
        }

        // Check if popup was closed without completing auth (user cancelled)
        const popupCheckInterval = setInterval(() => {
            // Double-check authFlowCompleted and callbackInvoked to prevent race conditions
            if (authWindow?.closed && !authFlowCompleted && !callbackInvoked) {
                authFlowCompleted = true
                clearInterval(popupCheckInterval)
                window.removeEventListener('message', handleAuthMessage)
                // User closed the popup without completing - just reset the loading state
                // Don't show error since this is a user-initiated cancellation
                invokeErrorCallback('')
            }
        }, 500)

        function handleAuthMessage(event: MessageEvent) {
            try {
                if (event.origin !== window.location.origin) return

                if (event.data.type === 'NYLAS_AUTH_SUCCESS') {
                    authFlowCompleted = true
                    clearInterval(popupCheckInterval)
                    window.removeEventListener('message', handleAuthMessage)
                    authWindow?.close()
                    NylasEmailClient.getAccounts()
                        .then(({ accounts }) => {
                            invokeSuccessCallback(accounts)
                        })
                        .catch((error) => {
                            console.error('Error fetching accounts after auth:', error)
                            invokeErrorCallback('Email connected but failed to fetch accounts. Please refresh the page.')
                        })
                } else if (event.data.type === 'NYLAS_AUTH_ERROR') {
                    authFlowCompleted = true
                    clearInterval(popupCheckInterval)
                    window.removeEventListener('message', handleAuthMessage)
                    authWindow?.close()
                    const errorMessage = event.data.error || 'Failed to add email'
                    invokeErrorCallback(errorMessage)
                }
            } catch (error) {
                console.error('Error processing auth message:', error)
                authFlowCompleted = true
                clearInterval(popupCheckInterval)
                window.removeEventListener('message', handleAuthMessage)
                invokeErrorCallback('Failed to add email')
            }
        }

        window.addEventListener('message', handleAuthMessage)
    } catch (error: unknown) {
        console.error('Error adding email:', error)
        const errorMessage = (error as Error).message || 'Failed to add email'
        showToast?.(errorMessage, 'danger')
        onError?.(errorMessage)
    }
}
